import React, { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  UserCircle, 
  Building, 
  Phone, 
  Mail, 
  X, 
  Check, 
  AlertCircle, 
  RefreshCw, 
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  FileText,
  Clock,
  AlertTriangle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  Download,
  Eye,
  Users,
  Activity,
  BarChart3,
  ChevronRight,
  CreditCard,
  Settings,
  Bell,
  Percent,
  Package,
  Truck,
  Receipt,
  Repeat,
  Star,
  Tag
} from "lucide-react";
import axios from "axios";

const Customers = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [lookupLoading, setLookupLoading] = useState(false);
  const [viewMode, setViewMode] = useState("table"); // table or cards
  const [detailCustomer, setDetailCustomer] = useState(null);
  
  // Multi-select
  const [selectedCustomers, setSelectedCustomers] = useState(new Set());
  
  // Sorting
  const [sortColumn, setSortColumn] = useState("total_revenue");
  const [sortDirection, setSortDirection] = useState("desc");
  
  // Filters
  const [filterStatus, setFilterStatus] = useState("all");
  const [columnFilters, setColumnFilters] = useState({
    name: "",
    org_number: "",
    city: "",
    email: ""
  });
  
  // Custom payment terms
  const [customPaymentTerms, setCustomPaymentTerms] = useState([]);
  const [showCustomPaymentTerms, setShowCustomPaymentTerms] = useState(false);
  const [newCustomTerm, setNewCustomTerm] = useState("");
  
  // Subscription dialog
  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedCustomerForSubscription, setSelectedCustomerForSubscription] = useState(null);
  const [subscriptionFormData, setSubscriptionFormData] = useState({
    interval: "monthly",
    start_date: new Date().toISOString().split('T')[0],
    end_date: "",
    lines: [{ article_id: "", description: "", quantity: 1, unit_price: 0, vat_rate: 25 }],
    notes: ""
  });
  
  const [formData, setFormData] = useState({
    // Basic info
    customer_number: "",
    name: "",
    org_number: "",
    vat_number: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: "",
    country: "Sverige",
    contact_person: "",
    
    // Payment & Invoicing
    payment_terms: 30,
    payment_terms_type: "net_days",
    invoice_email: "", // Separate email for invoices
    invoice_method: "email", // email, mail, efaktura, both
    currency: "SEK",
    price_list: "standard", // standard, vip, wholesale
    discount_percent: 0,
    credit_limit: "",
    reverse_vat: false, // Omvänd skattskyldighet
    
    // Subscription defaults
    default_subscription_interval: "monthly",
    default_subscription_articles: [], // Pre-selected articles for subscriptions
    auto_create_subscription: false,
    
    // Delivery
    delivery_address: "",
    delivery_postal_code: "",
    delivery_city: "",
    delivery_country: "Sverige",
    delivery_instructions: "",
    same_as_billing: true,
    
    // Reminders & Collection
    send_reminders: true,
    reminder_days: 14,
    reminder_fee: 60,
    send_to_collection: true,
    collection_days: 30,
    
    // Notes & Tags
    internal_notes: "",
    tags: [],
    customer_category: "", // prospect, active, vip, inactive
    
    // GLN/EDI for e-invoicing
    gln_number: "",
    edi_id: "",
  });
  
  const [activeTab, setActiveTab] = useState("basic");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [customersRes, articlesRes] = await Promise.all([
        axios.get(`${API}/customers/stats/overview`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/articles`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setCustomers(customersRes.data);
      setArticles(articlesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      // Fallback to regular customers endpoint
      try {
        const customersRes = await axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } });
        setCustomers(customersRes.data.map(c => ({
          ...c,
          total_revenue: 0,
          invoice_count: 0,
          outstanding_amount: 0,
          customer_status: "unknown"
        })));
      } catch (e) {
        toast.error("Kunde inte hämta kunder");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = async () => {
    if (!formData.org_number || formData.org_number.length < 10) {
      toast.error("Ange ett giltigt organisationsnummer");
      return;
    }
    
    setLookupLoading(true);
    try {
      const response = await axios.get(`${API}/customers/lookup/${formData.org_number}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setFormData(prev => ({
          ...prev,
          name: response.data.name || prev.name,
          address: response.data.address || prev.address,
          postal_code: response.data.postal_code || prev.postal_code,
          city: response.data.city || prev.city,
          vat_number: response.data.vat_number || prev.vat_number,
        }));
        toast.success("Företagsinfo hämtad!");
      }
    } catch (error) {
      toast.error("Kunde inte hitta företaget");
    } finally {
      setLookupLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await axios.put(`${API}/customers/${editingCustomer.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customers.updated'));
      } else {
        await axios.post(`${API}/customers`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success(t('customers.created'));
      }
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleDelete = async (customerId) => {
    try {
      await axios.delete(`${API}/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(t('customers.deleted'));
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCustomers.size === 0) return;
    
    let deleted = 0;
    let failed = 0;
    
    for (const id of selectedCustomers) {
      try {
        await axios.delete(`${API}/customers/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deleted++;
      } catch {
        failed++;
      }
    }
    
    if (deleted > 0) toast.success(`${deleted} kunder borttagna`);
    if (failed > 0) toast.error(`${failed} kunde inte tas bort`);
    
    setSelectedCustomers(new Set());
    fetchData();
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      // Basic info
      customer_number: customer.customer_number || "",
      name: customer.name || "",
      org_number: customer.org_number || "",
      vat_number: customer.vat_number || "",
      email: customer.email || "",
      phone: customer.phone || "",
      address: customer.address || "",
      postal_code: customer.postal_code || "",
      city: customer.city || "",
      country: customer.country || "Sverige",
      contact_person: customer.contact_person || "",
      
      // Payment & Invoicing
      payment_terms: customer.payment_terms || 30,
      payment_terms_type: customer.payment_terms_type || "net_days",
      invoice_email: customer.invoice_email || "",
      invoice_method: customer.invoice_method || "email",
      currency: customer.currency || "SEK",
      price_list: customer.price_list || "standard",
      discount_percent: customer.discount_percent || 0,
      credit_limit: customer.credit_limit || "",
      reverse_vat: customer.reverse_vat || false,
      
      // Subscription defaults
      default_subscription_interval: customer.default_subscription_interval || "monthly",
      default_subscription_articles: customer.default_subscription_articles || [],
      auto_create_subscription: customer.auto_create_subscription || false,
      
      // Delivery
      delivery_address: customer.delivery_address || "",
      delivery_postal_code: customer.delivery_postal_code || "",
      delivery_city: customer.delivery_city || "",
      delivery_country: customer.delivery_country || "Sverige",
      delivery_instructions: customer.delivery_instructions || "",
      same_as_billing: customer.same_as_billing !== false,
      
      // Reminders & Collection
      send_reminders: customer.send_reminders !== false,
      reminder_days: customer.reminder_days || 14,
      reminder_fee: customer.reminder_fee || 60,
      send_to_collection: customer.send_to_collection !== false,
      collection_days: customer.collection_days || 30,
      
      // Notes & Tags
      internal_notes: customer.internal_notes || "",
      tags: customer.tags || [],
      customer_category: customer.customer_category || "",
      
      // GLN/EDI
      gln_number: customer.gln_number || "",
      edi_id: customer.edi_id || "",
    });
    setActiveTab("basic");
    setDialogOpen(true);
  };

  const resetForm = async () => {
    setEditingCustomer(null);
    
    // Fetch next available customer number
    let nextNumber = "";
    try {
      const response = await axios.get(`${API}/customers/next-number`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      nextNumber = response.data.next_number;
    } catch (error) {
      console.error("Error fetching next customer number:", error);
    }
    
    setFormData({
      // Basic info
      customer_number: nextNumber,
      name: "",
      org_number: "",
      vat_number: "",
      email: "",
      phone: "",
      address: "",
      postal_code: "",
      city: "",
      country: "Sverige",
      contact_person: "",
      
      // Payment & Invoicing
      payment_terms: 30,
      payment_terms_type: "net_days",
      invoice_email: "",
      invoice_method: "email",
      currency: "SEK",
      price_list: "standard",
      discount_percent: 0,
      credit_limit: "",
      reverse_vat: false,
      
      // Subscription defaults
      default_subscription_interval: "monthly",
      default_subscription_articles: [],
      auto_create_subscription: false,
      
      // Delivery
      delivery_address: "",
      delivery_postal_code: "",
      delivery_city: "",
      delivery_country: "Sverige",
      delivery_instructions: "",
      same_as_billing: true,
      
      // Reminders & Collection
      send_reminders: true,
      reminder_days: 14,
      reminder_fee: 60,
      send_to_collection: true,
      collection_days: 30,
      
      // Notes & Tags
      internal_notes: "",
      tags: [],
      customer_category: "",
      
      // GLN/EDI
      gln_number: "",
      edi_id: "",
    });
    setActiveTab("basic");
    setShowCustomPaymentTerms(false);
    setNewCustomTerm("");
  };

  // Subscription functions
  const openSubscriptionDialog = (customer) => {
    setSelectedCustomerForSubscription(customer);
    setSubscriptionFormData({
      interval: "monthly",
      start_date: new Date().toISOString().split('T')[0],
      end_date: "",
      lines: [{ article_id: "", description: "", quantity: 1, unit_price: 0, vat_rate: 25 }],
      notes: ""
    });
    setSubscriptionDialogOpen(true);
  };

  const handleCreateSubscription = async () => {
    if (!selectedCustomerForSubscription) return;
    
    if (subscriptionFormData.lines.length === 0 || !subscriptionFormData.lines[0].description) {
      toast.error("Lägg till minst en rad");
      return;
    }

    try {
      const subtotal = subscriptionFormData.lines.reduce((sum, line) => sum + (line.quantity * line.unit_price), 0);
      const vat_amount = subscriptionFormData.lines.reduce((sum, line) => 
        sum + (line.quantity * line.unit_price * (line.vat_rate / 100)), 0);
      const total = subtotal + vat_amount;

      const startDate = new Date(subscriptionFormData.start_date);
      const dueDate = new Date(startDate);
      dueDate.setDate(dueDate.getDate() + 30);
      
      const nextDate = new Date(startDate);
      if (subscriptionFormData.interval === "monthly") nextDate.setMonth(nextDate.getMonth() + 1);
      else if (subscriptionFormData.interval === "quarterly") nextDate.setMonth(nextDate.getMonth() + 3);
      else if (subscriptionFormData.interval === "yearly") nextDate.setFullYear(nextDate.getFullYear() + 1);

      const invoiceData = {
        customer_id: selectedCustomerForSubscription.id,
        customer_name: selectedCustomerForSubscription.name,
        invoice_date: subscriptionFormData.start_date,
        due_date: dueDate.toISOString().split('T')[0],
        lines: subscriptionFormData.lines.map(line => ({
          ...line,
          total: line.quantity * line.unit_price * (1 + line.vat_rate / 100)
        })),
        subtotal,
        vat_amount,
        total,
        notes: subscriptionFormData.notes,
        status: "draft",
        is_recurring: true,
        recurring_interval: subscriptionFormData.interval,
        recurring_end_date: subscriptionFormData.end_date || null,
        next_recurring_date: nextDate.toISOString().split('T')[0]
      };

      await axios.post(`${API}/sales-invoices`, invoiceData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(`Abonnemang skapat för ${selectedCustomerForSubscription.name}!`);
      setSubscriptionDialogOpen(false);
      setSelectedCustomerForSubscription(null);
    } catch (error) {
      toast.error("Kunde inte skapa abonnemang");
    }
  };

  // Sorting
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("desc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn !== column) return <ArrowUpDown size={14} className="ml-1 text-slate-400" />;
    return sortDirection === "asc" 
      ? <ArrowUp size={14} className="ml-1 text-indigo-600" />
      : <ArrowDown size={14} className="ml-1 text-indigo-600" />;
  };

  // Selection
  const toggleSelectAll = () => {
    if (selectedCustomers.size === filteredCustomers.length) {
      setSelectedCustomers(new Set());
    } else {
      setSelectedCustomers(new Set(filteredCustomers.map(c => c.id)));
    }
  };

  const toggleCustomer = (id) => {
    const newSelected = new Set(selectedCustomers);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedCustomers(newSelected);
  };

  // Filtering and sorting
  const filteredCustomers = useMemo(() => {
    return customers
      .filter(c => {
        const matchesSearch = !searchTerm || 
          c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.org_number?.includes(searchTerm) ||
          c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.city?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = filterStatus === "all" || c.customer_status === filterStatus;
        
        const matchesName = !columnFilters.name || 
          c.name?.toLowerCase().includes(columnFilters.name.toLowerCase());
        const matchesOrg = !columnFilters.org_number || 
          c.org_number?.includes(columnFilters.org_number);
        const matchesCity = !columnFilters.city || 
          c.city?.toLowerCase().includes(columnFilters.city.toLowerCase());
        const matchesEmail = !columnFilters.email || 
          c.email?.toLowerCase().includes(columnFilters.email.toLowerCase());
        
        return matchesSearch && matchesStatus && matchesName && matchesOrg && matchesCity && matchesEmail;
      })
      .sort((a, b) => {
        let aVal = a[sortColumn] ?? 0;
        let bVal = b[sortColumn] ?? 0;
        
        if (typeof aVal === 'string') aVal = aVal.toLowerCase();
        if (typeof bVal === 'string') bVal = bVal.toLowerCase();
        
        if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
        if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
        return 0;
      });
  }, [customers, searchTerm, filterStatus, columnFilters, sortColumn, sortDirection]);

  // Statistics
  const stats = useMemo(() => {
    const total = customers.length;
    const active = customers.filter(c => c.customer_status === "active").length;
    const dormant = customers.filter(c => c.customer_status === "dormant").length;
    const inactive = customers.filter(c => c.customer_status === "inactive").length;
    const totalRevenue = customers.reduce((sum, c) => sum + (c.total_revenue || 0), 0);
    const outstanding = customers.reduce((sum, c) => sum + (c.outstanding_amount || 0), 0);
    const avgRevenue = total > 0 ? totalRevenue / total : 0;
    const overdue = customers.filter(c => c.overdue_count > 0).length;
    
    return { total, active, dormant, inactive, totalRevenue, outstanding, avgRevenue, overdue };
  }, [customers]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("sv-SE", {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active": return <Badge className="bg-green-100 text-green-700">Aktiv</Badge>;
      case "dormant": return <Badge className="bg-amber-100 text-amber-700">Vilande</Badge>;
      case "inactive": return <Badge className="bg-slate-100 text-slate-600">Inaktiv</Badge>;
      default: return <Badge variant="outline">Okänd</Badge>;
    }
  };

  const exportCustomers = () => {
    const csv = [
      ["Namn", "Org.nr", "E-post", "Telefon", "Stad", "Omsättning", "Fakturor", "Utestående", "Status"].join(";"),
      ...filteredCustomers.map(c => [
        c.name,
        c.org_number,
        c.email,
        c.phone,
        c.city,
        c.total_revenue,
        c.invoice_count,
        c.outstanding_amount,
        c.customer_status
      ].join(";"))
    ].join("\n");
    
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `kunder_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export klar!");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-indigo-600" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('customers.title')}</h1>
          <p className="text-slate-600">Komplett översikt över alla dina kunder</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCustomers}>
            <Download size={16} className="mr-2" />
            Exportera
          </Button>
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }} 
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="add-customer-btn"
          >
            <Plus size={16} className="mr-2" />
            {t('customers.addCustomer')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Card className="col-span-2">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Users className="text-indigo-600" size={20} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.total}</p>
                <p className="text-xs text-slate-500">Totalt kunder</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Activity className="text-green-600" size={16} />
              <div>
                <p className="text-xl font-bold text-green-600">{stats.active}</p>
                <p className="text-xs text-slate-500">Aktiva</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-2">
              <Clock className="text-amber-600" size={16} />
              <div>
                <p className="text-xl font-bold text-amber-600">{stats.dormant}</p>
                <p className="text-xs text-slate-500">Vilande</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="col-span-2">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <TrendingUp className="text-green-600" size={20} />
              </div>
              <div>
                <p className="text-lg font-bold text-green-600">{formatCurrency(stats.totalRevenue)}</p>
                <p className="text-xs text-slate-500">Total omsättning</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="col-span-2">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertTriangle className="text-red-600" size={20} />
              </div>
              <div>
                <p className="text-lg font-bold text-red-600">{formatCurrency(stats.outstanding)}</p>
                <p className="text-xs text-slate-500">Utestående</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions Bar */}
      {selectedCustomers.size > 0 && (
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="py-3">
            <div className="flex items-center justify-between">
              <span className="font-medium text-indigo-900">
                {selectedCustomers.size} kunder valda
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setSelectedCustomers(new Set())}>
                  Avmarkera
                </Button>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="text-red-600 border-red-300"
                  onClick={handleBulkDelete}
                >
                  <Trash2 size={14} className="mr-1" />
                  Ta bort valda
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters & Search */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input 
                placeholder="Sök kund, org.nr, e-post, stad..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alla status</SelectItem>
                <SelectItem value="active">Aktiva</SelectItem>
                <SelectItem value="dormant">Vilande</SelectItem>
                <SelectItem value="inactive">Inaktiva</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => {
                setSearchTerm("");
                setFilterStatus("all");
                setColumnFilters({ name: "", org_number: "", city: "", email: "" });
              }}
            >
              <X size={14} className="mr-1" />
              Rensa filter
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Customers Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between">
            <span>Kundlista ({filteredCustomers.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredCustomers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="p-3 w-10">
                      <Checkbox 
                        checked={selectedCustomers.size === filteredCustomers.length && filteredCustomers.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("name")}>
                        Namn {getSortIcon("name")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("org_number")}>
                        Org.nr {getSortIcon("org_number")}
                      </button>
                    </th>
                    <th className="text-left p-3">
                      <button className="flex items-center hover:text-indigo-600 font-medium" onClick={() => handleSort("city")}>
                        Stad {getSortIcon("city")}
                      </button>
                    </th>
                    <th className="text-right p-3">
                      <button className="flex items-center justify-end hover:text-indigo-600 font-medium w-full" onClick={() => handleSort("total_revenue")}>
                        Omsättning {getSortIcon("total_revenue")}
                      </button>
                    </th>
                    <th className="text-right p-3">
                      <button className="flex items-center justify-end hover:text-indigo-600 font-medium w-full" onClick={() => handleSort("invoice_count")}>
                        Fakturor {getSortIcon("invoice_count")}
                      </button>
                    </th>
                    <th className="text-right p-3">
                      <button className="flex items-center justify-end hover:text-indigo-600 font-medium w-full" onClick={() => handleSort("outstanding_amount")}>
                        Utestående {getSortIcon("outstanding_amount")}
                      </button>
                    </th>
                    <th className="text-right p-3">
                      <button className="flex items-center justify-end hover:text-indigo-600 font-medium w-full" onClick={() => handleSort("avg_days_to_pay")}>
                        Bet.tid {getSortIcon("avg_days_to_pay")}
                      </button>
                    </th>
                    <th className="text-center p-3">
                      <button className="flex items-center justify-center hover:text-indigo-600 font-medium w-full" onClick={() => handleSort("customer_status")}>
                        Status {getSortIcon("customer_status")}
                      </button>
                    </th>
                    <th className="text-right p-3 font-medium">Åtgärder</th>
                  </tr>
                  {/* Column Filters */}
                  <tr className="bg-slate-100/50">
                    <th className="p-1"></th>
                    <th className="p-1">
                      <Input 
                        placeholder="Sök..." 
                        value={columnFilters.name}
                        onChange={(e) => setColumnFilters({...columnFilters, name: e.target.value})}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1">
                      <Input 
                        placeholder="Sök..." 
                        value={columnFilters.org_number}
                        onChange={(e) => setColumnFilters({...columnFilters, org_number: e.target.value})}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1">
                      <Input 
                        placeholder="Sök..." 
                        value={columnFilters.city}
                        onChange={(e) => setColumnFilters({...columnFilters, city: e.target.value})}
                        className="h-7 text-xs"
                      />
                    </th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                    <th className="p-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers.map((customer) => (
                    <tr 
                      key={customer.id} 
                      className={`border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${selectedCustomers.has(customer.id) ? 'bg-indigo-50' : ''}`}
                    >
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        <Checkbox 
                          checked={selectedCustomers.has(customer.id)}
                          onCheckedChange={() => toggleCustomer(customer.id)}
                        />
                      </td>
                      <td className="p-3" onClick={() => setDetailCustomer(customer)}>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                            <Building className="text-indigo-600" size={14} />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{customer.name}</p>
                            {customer.email && <p className="text-xs text-slate-500">{customer.email}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="p-3 font-mono text-xs text-slate-600">{customer.org_number || "-"}</td>
                      <td className="p-3 text-slate-600">{customer.city || "-"}</td>
                      <td className="p-3 text-right">
                        <span className={`font-medium ${customer.total_revenue > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                          {formatCurrency(customer.total_revenue)}
                        </span>
                      </td>
                      <td className="p-3 text-right">
                        <span className="text-slate-600">{customer.invoice_count || 0}</span>
                      </td>
                      <td className="p-3 text-right">
                        {customer.outstanding_amount > 0 ? (
                          <span className="text-red-600 font-medium">{formatCurrency(customer.outstanding_amount)}</span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {customer.avg_days_to_pay > 0 ? (
                          <span className={customer.avg_days_to_pay > 30 ? 'text-amber-600' : 'text-slate-600'}>
                            {customer.avg_days_to_pay} dagar
                          </span>
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">{getStatusBadge(customer.customer_status)}</td>
                      <td className="p-3">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => setDetailCustomer(customer)} title="Visa detaljer">
                            <Eye size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(customer)} title="Redigera">
                            <Edit size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => openSubscriptionDialog(customer)}
                            className="text-indigo-600"
                            title="Nytt abonnemang"
                          >
                            <Calendar size={14} />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(customer.id)}
                            className="text-red-500"
                            title="Ta bort"
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-16 text-slate-500">
              <Users className="mx-auto mb-4 text-slate-300" size={48} />
              <p>Inga kunder hittades</p>
              <p className="text-sm mt-1">Lägg till din första kund för att komma igång</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Customer Detail Dialog */}
      <Dialog open={!!detailCustomer} onOpenChange={() => setDetailCustomer(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Building className="text-indigo-600" size={20} />
              </div>
              {detailCustomer?.name}
            </DialogTitle>
          </DialogHeader>
          
          {detailCustomer && (
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Org.nummer</Label>
                  <p className="font-mono">{detailCustomer.org_number || "-"}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Momsreg.nr</Label>
                  <p className="font-mono">{detailCustomer.vat_number || "-"}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">E-post</Label>
                  <p>{detailCustomer.email || "-"}</p>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Telefon</Label>
                  <p>{detailCustomer.phone || "-"}</p>
                </div>
                <div className="col-span-2 space-y-1">
                  <Label className="text-xs text-slate-500">Adress</Label>
                  <p>{detailCustomer.address}, {detailCustomer.postal_code} {detailCustomer.city}</p>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-4 gap-3">
                <div className="bg-green-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(detailCustomer.total_revenue)}</p>
                  <p className="text-xs text-green-700">Total omsättning</p>
                </div>
                <div className="bg-indigo-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-indigo-600">{detailCustomer.invoice_count}</p>
                  <p className="text-xs text-indigo-700">Fakturor</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(detailCustomer.outstanding_amount)}</p>
                  <p className="text-xs text-red-700">Utestående</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg text-center">
                  <p className="text-2xl font-bold text-amber-600">{detailCustomer.avg_days_to_pay || 0}</p>
                  <p className="text-xs text-amber-700">Snitt bet.dagar</p>
                </div>
              </div>

              {/* Payment Terms */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <span>Betalningsvillkor</span>
                <span className="font-medium">{detailCustomer.payment_terms} dagar</span>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setDetailCustomer(null); handleEdit(detailCustomer); }}>
                  <Edit size={14} className="mr-2" />
                  Redigera
                </Button>
                <Button className="flex-1 bg-indigo-600 hover:bg-indigo-700" onClick={() => { setDetailCustomer(null); openSubscriptionDialog(detailCustomer); }}>
                  <Calendar size={14} className="mr-2" />
                  Nytt abonnemang
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Customer Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {editingCustomer ? t('customers.editCustomer') : t('customers.addCustomer')}
            </DialogTitle>
            <DialogDescription>
              {editingCustomer ? "Uppdatera kundinformation" : "Fyll i organisationsnummer för att hämta företagsinfo automatiskt"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 overflow-hidden">
              <TabsList className="grid w-full grid-cols-5 mb-4">
                <TabsTrigger value="basic" className="flex items-center gap-1 text-xs">
                  <Building size={14} />
                  Grundinfo
                </TabsTrigger>
                <TabsTrigger value="invoicing" className="flex items-center gap-1 text-xs">
                  <Receipt size={14} />
                  Fakturering
                </TabsTrigger>
                <TabsTrigger value="subscription" className="flex items-center gap-1 text-xs">
                  <Repeat size={14} />
                  Abonnemang
                </TabsTrigger>
                <TabsTrigger value="delivery" className="flex items-center gap-1 text-xs">
                  <Truck size={14} />
                  Leverans
                </TabsTrigger>
                <TabsTrigger value="settings" className="flex items-center gap-1 text-xs">
                  <Settings size={14} />
                  Övrigt
                </TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto pr-2">
                {/* TAB 1: Basic Info */}
                <TabsContent value="basic" className="space-y-4 mt-0">
                  {/* Customer Number & Org Number */}
                  <div className="flex gap-4">
                    <div className="w-32 space-y-2">
                      <Label>Kundnr</Label>
                      <Input
                        value={formData.customer_number}
                        onChange={(e) => setFormData({ ...formData, customer_number: e.target.value })}
                        placeholder="1001"
                        className="font-mono"
                        data-testid="customer-number"
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <Label>Organisationsnummer</Label>
                      <div className="flex gap-2">
                        <Input
                          value={formData.org_number}
                          onChange={(e) => setFormData({ ...formData, org_number: e.target.value })}
                          placeholder="XXXXXX-XXXX"
                          data-testid="customer-org-number"
                        />
                        <Button type="button" variant="outline" onClick={handleLookup} disabled={lookupLoading}>
                          {lookupLoading ? <RefreshCw className="animate-spin" size={16} /> : <Search size={16} />}
                          <span className="ml-2">Hämta info</span>
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2 space-y-2">
                      <Label>Företagsnamn *</Label>
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        data-testid="customer-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>E-post</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="customer-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefon</Label>
                      <Input
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        data-testid="customer-phone"
                      />
                    </div>
                    <div className="col-span-2 space-y-2">
                      <Label>Adress</Label>
                      <Input
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        data-testid="customer-address"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Postnummer</Label>
                      <Input
                        value={formData.postal_code}
                        onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                        data-testid="customer-postal-code"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Stad</Label>
                      <Input
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        data-testid="customer-city"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Kontaktperson</Label>
                      <Input
                        value={formData.contact_person}
                        onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                        data-testid="customer-contact-person"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Kundkategori</Label>
                      <Select
                        value={formData.customer_category}
                        onValueChange={(v) => setFormData({ ...formData, customer_category: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Välj kategori..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="prospect">Prospekt</SelectItem>
                          <SelectItem value="active">Aktiv kund</SelectItem>
                          <SelectItem value="vip">VIP-kund</SelectItem>
                          <SelectItem value="inactive">Inaktiv</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </TabsContent>

                {/* TAB 2: Invoicing */}
                <TabsContent value="invoicing" className="space-y-4 mt-0">
                  <div className="p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                    <h4 className="font-medium text-indigo-900 flex items-center gap-2 mb-1">
                      <CreditCard size={16} />
                      Betalningsvillkor
                    </h4>
                    <p className="text-xs text-indigo-700">Standardvillkor som används för alla fakturor till denna kund</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Villkorstyp</Label>
                      <Select
                        value={formData.payment_terms_type}
                        onValueChange={(v) => {
                          let defaultDays = formData.payment_terms;
                          if (v === "prepaid") defaultDays = -1;
                          else if (v === "on_delivery") defaultDays = 0;
                          else if (v === "net_days" && formData.payment_terms < 0) defaultDays = 30;
                          else if (v === "end_of_month" && formData.payment_terms < 0) defaultDays = 30;
                          setFormData({ ...formData, payment_terms_type: v, payment_terms: defaultDays });
                        }}
                      >
                        <SelectTrigger data-testid="customer-payment-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="net_days">Netto dagar</SelectItem>
                          <SelectItem value="end_of_month">Löpande månad</SelectItem>
                          <SelectItem value="prepaid">Förskott</SelectItem>
                          <SelectItem value="on_delivery">Vid leverans</SelectItem>
                          <SelectItem value="installments">Delbetalning</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {(formData.payment_terms_type === "net_days" || formData.payment_terms_type === "end_of_month") && (
                      <div className="space-y-2">
                        <Label>Antal dagar</Label>
                        <Select
                          value={formData.payment_terms.toString()}
                          onValueChange={(v) => {
                            if (v === "custom") {
                              setShowCustomPaymentTerms(true);
                            } else {
                              setFormData({ ...formData, payment_terms: parseInt(v) });
                            }
                          }}
                        >
                          <SelectTrigger data-testid="customer-payment-terms">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {formData.payment_terms_type === "net_days" ? (
                              <>
                                <SelectItem value="0">Kontant (0 dagar)</SelectItem>
                                <SelectItem value="10">10 dagar</SelectItem>
                                <SelectItem value="14">14 dagar</SelectItem>
                                <SelectItem value="20">20 dagar</SelectItem>
                                <SelectItem value="30">30 dagar (standard)</SelectItem>
                                <SelectItem value="45">45 dagar</SelectItem>
                                <SelectItem value="60">60 dagar</SelectItem>
                                <SelectItem value="90">90 dagar</SelectItem>
                              </>
                            ) : (
                              <>
                                <SelectItem value="0">Sista dagen i månaden</SelectItem>
                                <SelectItem value="15">+ 15 dagar</SelectItem>
                                <SelectItem value="30">+ 30 dagar</SelectItem>
                                <SelectItem value="45">+ 45 dagar</SelectItem>
                              </>
                            )}
                            {customPaymentTerms.map(term => (
                              <SelectItem key={term} value={term.toString()}>{term} dagar</SelectItem>
                            ))}
                            <SelectItem value="custom" className="text-indigo-600">+ Egen...</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {showCustomPaymentTerms && (
                    <div className="flex gap-2 items-end p-3 bg-slate-50 rounded-lg border">
                      <div className="flex-1">
                        <Label className="text-xs">Ange antal dagar</Label>
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          value={newCustomTerm}
                          onChange={(e) => setNewCustomTerm(e.target.value)}
                          className="h-9"
                        />
                      </div>
                      <Button type="button" size="sm" onClick={() => {
                        const days = parseInt(newCustomTerm);
                        if (days > 0 && days <= 365) {
                          if (!customPaymentTerms.includes(days)) {
                            setCustomPaymentTerms(prev => [...prev, days].sort((a,b) => a-b));
                          }
                          setFormData({ ...formData, payment_terms: days });
                          setShowCustomPaymentTerms(false);
                          setNewCustomTerm("");
                        }
                      }} className="bg-indigo-600">
                        <Check size={14} />
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={() => setShowCustomPaymentTerms(false)}>
                        <X size={14} />
                      </Button>
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Fakturametod</Label>
                      <Select
                        value={formData.invoice_method}
                        onValueChange={(v) => setFormData({ ...formData, invoice_method: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">E-post</SelectItem>
                          <SelectItem value="mail">Brev</SelectItem>
                          <SelectItem value="efaktura">E-faktura (Peppol)</SelectItem>
                          <SelectItem value="both">E-post + Brev</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Faktura-email (om annan)</Label>
                      <Input
                        type="email"
                        value={formData.invoice_email}
                        onChange={(e) => setFormData({ ...formData, invoice_email: e.target.value })}
                        placeholder="faktura@foretag.se"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Valuta</Label>
                      <Select
                        value={formData.currency}
                        onValueChange={(v) => setFormData({ ...formData, currency: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SEK">SEK - Svenska kronor</SelectItem>
                          <SelectItem value="EUR">EUR - Euro</SelectItem>
                          <SelectItem value="USD">USD - Dollar</SelectItem>
                          <SelectItem value="NOK">NOK - Norska kronor</SelectItem>
                          <SelectItem value="DKK">DKK - Danska kronor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Prislista</Label>
                      <Select
                        value={formData.price_list}
                        onValueChange={(v) => setFormData({ ...formData, price_list: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="standard">Standard</SelectItem>
                          <SelectItem value="vip">VIP (-10%)</SelectItem>
                          <SelectItem value="wholesale">Grossist (-20%)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-1">
                        <Percent size={14} />
                        Kundrabatt %
                      </Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={formData.discount_percent}
                        onChange={(e) => setFormData({ ...formData, discount_percent: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Kreditgräns (SEK)</Label>
                      <Input
                        type="number"
                        value={formData.credit_limit}
                        onChange={(e) => setFormData({ ...formData, credit_limit: e.target.value })}
                        placeholder="Ingen gräns"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <Switch
                      checked={formData.reverse_vat}
                      onCheckedChange={(checked) => setFormData({ ...formData, reverse_vat: checked })}
                    />
                    <div>
                      <Label className="font-medium">Omvänd skattskyldighet</Label>
                      <p className="text-xs text-amber-700">Aktivera för byggtjänster eller EU-handel</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>GLN-nummer (för e-faktura)</Label>
                      <Input
                        value={formData.gln_number}
                        onChange={(e) => setFormData({ ...formData, gln_number: e.target.value })}
                        placeholder="13-siffrigt nummer"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Momsregistreringsnr</Label>
                      <Input
                        value={formData.vat_number}
                        onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                        placeholder="SE123456789001"
                      />
                    </div>
                  </div>
                </TabsContent>

                {/* TAB 3: Subscription */}
                <TabsContent value="subscription" className="space-y-4 mt-0">
                  <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                    <h4 className="font-medium text-purple-900 flex items-center gap-2 mb-1">
                      <Repeat size={16} />
                      Abonnemangsinställningar
                    </h4>
                    <p className="text-xs text-purple-700">Förenkla skapande av återkommande fakturor för denna kund</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                      <Switch
                        checked={formData.auto_create_subscription}
                        onCheckedChange={(checked) => setFormData({ ...formData, auto_create_subscription: checked })}
                      />
                      <div>
                        <Label className="font-medium">Automatiskt abonnemang</Label>
                        <p className="text-xs text-slate-500">Skapa abonnemang automatiskt vid första faktura</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Standard fakturainterval</Label>
                      <Select
                        value={formData.default_subscription_interval}
                        onValueChange={(v) => setFormData({ ...formData, default_subscription_interval: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="weekly">Veckovis</SelectItem>
                          <SelectItem value="biweekly">Varannan vecka</SelectItem>
                          <SelectItem value="monthly">Månadsvis</SelectItem>
                          <SelectItem value="quarterly">Kvartalsvis</SelectItem>
                          <SelectItem value="biannually">Halvårsvis</SelectItem>
                          <SelectItem value="yearly">Årsvis</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Standardartiklar för abonnemang</Label>
                      <div className="border rounded-lg p-3 min-h-[100px] bg-white">
                        {formData.default_subscription_articles.length > 0 ? (
                          <div className="space-y-2">
                            {formData.default_subscription_articles.map((articleId, idx) => {
                              const article = articles.find(a => a.id === articleId);
                              return article ? (
                                <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                                  <span className="text-sm">{article.name}</span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setFormData({
                                      ...formData,
                                      default_subscription_articles: formData.default_subscription_articles.filter((_, i) => i !== idx)
                                    })}
                                  >
                                    <X size={14} />
                                  </Button>
                                </div>
                              ) : null;
                            })}
                          </div>
                        ) : (
                          <p className="text-sm text-slate-400 text-center py-4">
                            Inga standardartiklar valda
                          </p>
                        )}
                        <Select
                          onValueChange={(v) => {
                            if (!formData.default_subscription_articles.includes(v)) {
                              setFormData({
                                ...formData,
                                default_subscription_articles: [...formData.default_subscription_articles, v]
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="+ Lägg till artikel..." />
                          </SelectTrigger>
                          <SelectContent>
                            {articles.map(article => (
                              <SelectItem key={article.id} value={article.id}>
                                {article.name} - {article.price} kr
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <p className="text-xs text-slate-500">
                        Dessa artiklar förfylls automatiskt när du skapar ett nytt abonnemang för kunden
                      </p>
                    </div>
                  </div>
                </TabsContent>

                {/* TAB 4: Delivery */}
                <TabsContent value="delivery" className="space-y-4 mt-0">
                  <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                    <h4 className="font-medium text-green-900 flex items-center gap-2 mb-1">
                      <Truck size={16} />
                      Leveransadress
                    </h4>
                    <p className="text-xs text-green-700">Ange separat leveransadress om den skiljer sig från fakturaadressen</p>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                    <Switch
                      checked={formData.same_as_billing}
                      onCheckedChange={(checked) => setFormData({ ...formData, same_as_billing: checked })}
                    />
                    <div>
                      <Label className="font-medium">Samma som fakturaadress</Label>
                      <p className="text-xs text-slate-500">Använd kundens huvudadress för leveranser</p>
                    </div>
                  </div>

                  {!formData.same_as_billing && (
                    <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg bg-white">
                      <div className="col-span-2 space-y-2">
                        <Label>Leveransadress</Label>
                        <Input
                          value={formData.delivery_address}
                          onChange={(e) => setFormData({ ...formData, delivery_address: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Postnummer</Label>
                        <Input
                          value={formData.delivery_postal_code}
                          onChange={(e) => setFormData({ ...formData, delivery_postal_code: e.target.value })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Stad</Label>
                        <Input
                          value={formData.delivery_city}
                          onChange={(e) => setFormData({ ...formData, delivery_city: e.target.value })}
                        />
                      </div>
                      <div className="col-span-2 space-y-2">
                        <Label>Land</Label>
                        <Input
                          value={formData.delivery_country}
                          onChange={(e) => setFormData({ ...formData, delivery_country: e.target.value })}
                        />
                      </div>
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label>Leveransinstruktioner</Label>
                    <Textarea
                      value={formData.delivery_instructions}
                      onChange={(e) => setFormData({ ...formData, delivery_instructions: e.target.value })}
                      placeholder="T.ex. 'Ring vid ankomst', 'Lämna vid bakdörren', etc."
                      rows={3}
                    />
                  </div>
                </TabsContent>

                {/* TAB 5: Settings/Other */}
                <TabsContent value="settings" className="space-y-4 mt-0">
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <h4 className="font-medium text-amber-900 flex items-center gap-2 mb-1">
                      <Bell size={16} />
                      Påminnelser & Inkasso
                    </h4>
                    <p className="text-xs text-amber-700">Automatiska åtgärder vid förfallna fakturor</p>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={formData.send_reminders}
                          onCheckedChange={(checked) => setFormData({ ...formData, send_reminders: checked })}
                        />
                        <div>
                          <Label className="font-medium">Skicka påminnelser</Label>
                          <p className="text-xs text-slate-500">Automatiskt vid förfallna fakturor</p>
                        </div>
                      </div>
                      {formData.send_reminders && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">Efter</span>
                          <Input
                            type="number"
                            value={formData.reminder_days}
                            onChange={(e) => setFormData({ ...formData, reminder_days: parseInt(e.target.value) || 14 })}
                            className="w-16 h-8"
                          />
                          <span className="text-sm text-slate-600">dagar</span>
                        </div>
                      )}
                    </div>

                    {formData.send_reminders && (
                      <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border ml-6">
                        <Label>Påminnelseavgift</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={formData.reminder_fee}
                            onChange={(e) => setFormData({ ...formData, reminder_fee: parseInt(e.target.value) || 0 })}
                            className="w-20 h-8"
                          />
                          <span className="text-sm text-slate-600">kr</span>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={formData.send_to_collection}
                          onCheckedChange={(checked) => setFormData({ ...formData, send_to_collection: checked })}
                        />
                        <div>
                          <Label className="font-medium">Skicka till inkasso</Label>
                          <p className="text-xs text-slate-500">Automatiskt vid obetald faktura</p>
                        </div>
                      </div>
                      {formData.send_to_collection && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-600">Efter</span>
                          <Input
                            type="number"
                            value={formData.collection_days}
                            onChange={(e) => setFormData({ ...formData, collection_days: parseInt(e.target.value) || 30 })}
                            className="w-16 h-8"
                          />
                          <span className="text-sm text-slate-600">dagar</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="flex items-center gap-1">
                      <FileText size={14} />
                      Interna anteckningar
                    </Label>
                    <Textarea
                      value={formData.internal_notes}
                      onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
                      placeholder="Anteckningar som bara syns internt (visas aldrig för kunden)"
                      rows={4}
                    />
                  </div>

                  <div className="p-3 bg-slate-100 rounded-lg">
                    <p className="text-xs text-slate-500 text-center">
                      Fler inställningar kommer snart: Taggar, Anpassade fält, API-kopplingar...
                    </p>
                  </div>
                </TabsContent>
              </div>
            </Tabs>

            <DialogFooter className="pt-4 border-t mt-4">
              <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                Avbryt
              </Button>
              <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-customer-btn">
                {editingCustomer ? 'Spara ändringar' : 'Skapa kund'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Subscription Dialog */}
      <Dialog open={subscriptionDialogOpen} onOpenChange={setSubscriptionDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="text-indigo-600" size={20} />
              Nytt abonnemang
            </DialogTitle>
            <DialogDescription>
              Skapa ett återkommande abonnemang för {selectedCustomerForSubscription?.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-indigo-50 p-4 rounded-lg flex items-center gap-3">
              <Building className="text-indigo-600" size={20} />
              <div>
                <p className="font-medium">{selectedCustomerForSubscription?.name}</p>
                <p className="text-xs text-slate-500">{selectedCustomerForSubscription?.org_number}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Intervall</Label>
                <Select 
                  value={subscriptionFormData.interval} 
                  onValueChange={(v) => setSubscriptionFormData({...subscriptionFormData, interval: v})}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Månadsvis</SelectItem>
                    <SelectItem value="quarterly">Kvartalsvis</SelectItem>
                    <SelectItem value="yearly">Årsvis</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Startdatum</Label>
                <Input 
                  type="date" 
                  value={subscriptionFormData.start_date}
                  onChange={(e) => setSubscriptionFormData({...subscriptionFormData, start_date: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Slutdatum</Label>
                <Input 
                  type="date" 
                  value={subscriptionFormData.end_date}
                  onChange={(e) => setSubscriptionFormData({...subscriptionFormData, end_date: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Rader</Label>
                <Button 
                  type="button" 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSubscriptionFormData({
                    ...subscriptionFormData,
                    lines: [...subscriptionFormData.lines, { article_id: "", description: "", quantity: 1, unit_price: 0, vat_rate: 25 }]
                  })}
                >
                  <Plus size={14} className="mr-1" /> Lägg till
                </Button>
              </div>
              {subscriptionFormData.lines.map((line, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 p-3 bg-slate-50 rounded-lg items-end">
                  <div className="col-span-3">
                    <Label className="text-xs">Artikel</Label>
                    <Select 
                      value={line.article_id}
                      onValueChange={(v) => {
                        const article = articles.find(a => a.id === v);
                        const newLines = [...subscriptionFormData.lines];
                        newLines[idx] = {
                          ...newLines[idx],
                          article_id: v,
                          description: article?.name || "",
                          unit_price: article?.price || 0,
                          vat_rate: article?.vat_rate || 25
                        };
                        setSubscriptionFormData({...subscriptionFormData, lines: newLines});
                      }}
                    >
                      <SelectTrigger className="h-8"><SelectValue placeholder="Välj..." /></SelectTrigger>
                      <SelectContent>
                        {articles.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-4">
                    <Label className="text-xs">Beskrivning</Label>
                    <Input 
                      value={line.description}
                      onChange={(e) => {
                        const newLines = [...subscriptionFormData.lines];
                        newLines[idx].description = e.target.value;
                        setSubscriptionFormData({...subscriptionFormData, lines: newLines});
                      }}
                      className="h-8"
                    />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-xs">Antal</Label>
                    <Input 
                      type="number"
                      value={line.quantity}
                      onChange={(e) => {
                        const newLines = [...subscriptionFormData.lines];
                        newLines[idx].quantity = parseFloat(e.target.value) || 0;
                        setSubscriptionFormData({...subscriptionFormData, lines: newLines});
                      }}
                      className="h-8"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Pris</Label>
                    <Input 
                      type="number"
                      value={line.unit_price}
                      onChange={(e) => {
                        const newLines = [...subscriptionFormData.lines];
                        newLines[idx].unit_price = parseFloat(e.target.value) || 0;
                        setSubscriptionFormData({...subscriptionFormData, lines: newLines});
                      }}
                      className="h-8"
                    />
                  </div>
                  <div className="col-span-1">
                    <Label className="text-xs">Moms</Label>
                    <Input 
                      type="number"
                      value={line.vat_rate}
                      onChange={(e) => {
                        const newLines = [...subscriptionFormData.lines];
                        newLines[idx].vat_rate = parseFloat(e.target.value) || 0;
                        setSubscriptionFormData({...subscriptionFormData, lines: newLines});
                      }}
                      className="h-8"
                    />
                  </div>
                  <div className="col-span-1">
                    {subscriptionFormData.lines.length > 1 && (
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          const newLines = subscriptionFormData.lines.filter((_, i) => i !== idx);
                          setSubscriptionFormData({...subscriptionFormData, lines: newLines});
                        }}
                      >
                        <Trash2 size={14} className="text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-indigo-50 p-4 rounded-lg flex justify-between">
              <span>Summa per period:</span>
              <span className="font-bold text-indigo-600">
                {formatCurrency(subscriptionFormData.lines.reduce((sum, l) => 
                  sum + l.quantity * l.unit_price * (1 + l.vat_rate / 100), 0
                ))}
              </span>
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSubscriptionDialogOpen(false)}>Avbryt</Button>
              <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={handleCreateSubscription}>
                <Calendar size={14} className="mr-2" />
                Skapa abonnemang
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
